import { useState, useCallback } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { getUserProfile, updateUserProfile as putUserProfile, UserProfile } from '../../user/api';
import {
  RestUser,
  UserInput,
  UserProfileInput,
  CreateUserInput,
  RestApiError,
} from '../types/user';

// Server profile (camelCase) → the RestUser shape the app consumes.
function toRestUser(profile: UserProfile): RestUser {
  return {
    id: profile.id,
    email: profile.email || '',
    firstName: profile.firstName ?? undefined,
    lastName: profile.lastName ?? undefined,
    nickname: profile.nickname ?? undefined,
    picture_url: profile.pictureUrl ?? undefined,
    birthDate: profile.birthDate ?? undefined,
    accountPrivacy: profile.accountPrivacy ?? undefined,
    createdAt: profile.createdAt || new Date().toISOString(),
    lastSeenAt: profile.lastSeenAt ?? undefined,
  };
}

// Resolve the caller's access token + Cognito sub for the REST calls.
async function getAuth(): Promise<{ accessToken: string; userId: string }> {
  const session = await fetchAuthSession();
  if (!session.tokens?.accessToken) {
    throw new Error('No access token available. Please log in.');
  }
  const { userId } = await getCurrentUser();
  return { accessToken: session.tokens.accessToken.toString(), userId };
}

export function useUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);
  const [user, setUser] = useState<RestUser | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const fail = useCallback((err: any, fallback: string): null => {
    console.error(`❌ ${fallback}:`, err);
    setError({ message: err?.message || fallback, errorType: err?.errorType, errorInfo: err?.errorInfo });
    return null;
  }, []);

  // Get the current user's profile (REST GET /user/{sub}).
  const getUser = useCallback(async (): Promise<RestUser | null> => {
    setLoading(true);
    clearError();
    try {
      const { accessToken, userId } = await getAuth();
      const profile = await getUserProfile(userId, accessToken);
      if (!profile) {
        console.log('⚠️ User profile not found yet (may not be created)');
        return null;
      }
      const userData = toRestUser(profile);
      setUser(userData);
      return userData;
    } catch (err: any) {
      return fail(err, 'Failed to fetch user');
    } finally {
      setLoading(false);
    }
  }, [clearError, fail]);

  // Update the current user's profile (REST PUT /user/{sub}).
  const updateUser = useCallback(async (userInput: UserInput | UserProfileInput): Promise<RestUser | null> => {
    setLoading(true);
    clearError();
    try {
      const { accessToken, userId } = await getAuth();

      const profileInput: UserProfileInput = {
        firstName: (userInput as any).firstName,
        lastName: (userInput as any).lastName,
        nickname: userInput.nickname,
        picture: userInput.picture,
        birthDate: (userInput as any).birthDate,
        address: userInput.address,
        phoneNumber: (userInput as any).phoneNumber,
        locale: userInput.locale,
        zone: userInput.zone,
        // Was missing from this allowlist, so the privacy toggle was stripped
        // HERE and never reached the wire — the save succeeded and reported
        // success while the setting silently did not change (BUG-15).
        accountPrivacy: (userInput as UserProfileInput).accountPrivacy,
      };
      (Object.keys(profileInput) as (keyof UserProfileInput)[]).forEach((key) => {
        if (profileInput[key] === undefined) delete profileInput[key];
      });

      const profile = await putUserProfile(userId, accessToken, profileInput);
      if (!profile) throw new Error('The server did not return an updated profile.');
      const userData = toRestUser(profile);
      setUser(userData);
      return userData;
    } catch (err: any) {
      // Record for any UI observing hook state, then rethrow so the caller can
      // surface the specific server error (e.g. 403/500 detail) to the user.
      fail(err, 'Failed to update user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError, fail]);

  // Schema has no createUser — creation happens via updateUser (upsert).
  const createUser = useCallback(async (input: CreateUserInput): Promise<RestUser | null> => {
    return updateUser(input);
  }, [updateUser]);

  const updateUserProfile = useCallback(async (
    email: string,
    profileData: Partial<UserInput>
  ): Promise<RestUser | null> => {
    return updateUser({ email, ...profileData });
  }, [updateUser]);

  // Touch lastSeenAt — the server refreshes it on any profile write.
  const updateLastSeen = useCallback(async (userData: RestUser): Promise<RestUser | null> => {
    setLoading(true);
    clearError();
    try {
      const { accessToken, userId } = await getAuth();
      const profileInput: UserProfileInput = {};
      if (userData.firstName !== undefined) profileInput.firstName = userData.firstName;
      if (userData.lastName !== undefined) profileInput.lastName = userData.lastName;

      const profile = await putUserProfile(userId, accessToken, profileInput);
      if (!profile) return null;
      const updated = toRestUser(profile);
      setUser(updated);
      return updated;
    } catch (err: any) {
      return fail(err, 'Failed to update lastSeenAt');
    } finally {
      setLoading(false);
    }
  }, [clearError, fail]);

  const getCurrentSession = useCallback(async () => {
    try {
      return await fetchAuthSession();
    } catch (err) {
      console.error('❌ Failed to get current session:', err);
      return null;
    }
  }, []);

  const fetchCurrentUser = useCallback(async (): Promise<RestUser | null> => {
    return getUser();
  }, [getUser]);

  return {
    user,
    loading,
    error,
    getUser,
    createUser,
    updateUser,
    updateUserProfile,
    updateLastSeen,
    fetchCurrentUser,
    getCurrentSession,
    clearError,
    isAuthenticated: !!user,
  };
}
